/**
 * useAssignableRoles Hook (V37)
 *
 * 직원에게 부여 가능한 역할 목록을 조회합니다.
 * - admin 역할은 제외됨
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

export interface RoleInfo {
  id: string;
  name: string;
  description?: string;
}

export interface UseAssignableRolesResult {
  roles: RoleInfo[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAssignableRoles(): UseAssignableRolesResult {
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!functions) {
        // 데모 모드: 샘플 역할
        setRoles([
          { id: 'role_hr', name: '인사팀 담당자', description: '인사관리 권한' },
          { id: 'role_exec', name: '임원', description: '임원 전용 권한' },
          { id: 'org_manager', name: '조직 관리자', description: '조직 관리 권한' },
        ]);
        return;
      }

      const getAssignableRoles = httpsCallable<void, { roles: RoleInfo[] }>(
        functions,
        'getAssignableRoles'
      );

      const result = await getAssignableRoles();
      setRoles(result.data.roles);
    } catch (err: any) {
      console.error('[useAssignableRoles] 역할 조회 실패:', err);
      setError(err.message || '역할 목록 조회에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return {
    roles,
    isLoading,
    error,
    refetch: fetchRoles,
  };
}
